import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TrackingCode {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
  display_order: number;
}

export function TrackingCodeInjector() {
  const { data: trackingCodes = [] } = useQuery({
    queryKey: ['tracking-codes-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tracking_codes')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching tracking codes:', error);
        return [];
      }
      return data as TrackingCode[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Parse and inject scripts that need to run
  useEffect(() => {
    trackingCodes.forEach((tc) => {
      // Check if it contains script tags that need execution
      if (tc.code.includes('<script') && !tc.code.includes('src=')) {
        // Extract inline script content
        const scriptMatch = tc.code.match(/<script[^>]*>([\s\S]*?)<\/script>/gi);
        if (scriptMatch) {
          scriptMatch.forEach((scriptTag) => {
            const content = scriptTag.replace(/<\/?script[^>]*>/gi, '').trim();
            if (content) {
              try {
                // Create and execute script
                const script = document.createElement('script');
                script.textContent = content;
                script.dataset.trackingCode = tc.name;
                document.head.appendChild(script);
              } catch (error) {
                console.error(`Error executing tracking code "${tc.name}":`, error);
              }
            }
          });
        }
      }
    });

    // Cleanup function to remove scripts on unmount
    return () => {
      document.querySelectorAll('script[data-tracking-code]').forEach((el) => el.remove());
    };
  }, [trackingCodes]);

  if (trackingCodes.length === 0) return null;

  // Separate different types of tracking codes
  const metaTags: string[] = [];
  const linkTags: string[] = [];
  const externalScripts: string[] = [];
  const inlineScripts: string[] = [];

  trackingCodes.forEach((tc) => {
    // Extract meta tags
    const metaMatches = tc.code.match(/<meta[^>]*>/gi);
    if (metaMatches) metaTags.push(...metaMatches);

    // Extract link tags
    const linkMatches = tc.code.match(/<link[^>]*>/gi);
    if (linkMatches) linkTags.push(...linkMatches);

    // Extract external scripts (with src)
    const externalScriptMatches = tc.code.match(/<script[^>]*src=[^>]*><\/script>/gi);
    if (externalScriptMatches) externalScripts.push(...externalScriptMatches);
  });

  return (
    <Helmet>
      {/* Inject meta tags */}
      {metaTags.map((tag, i) => {
        const nameMatch = tag.match(/name=["']([^"']*)["']/);
        const contentMatch = tag.match(/content=["']([^"']*)["']/);
        const propertyMatch = tag.match(/property=["']([^"']*)["']/);
        
        if (propertyMatch && contentMatch) {
          return <meta key={`meta-${i}`} property={propertyMatch[1]} content={contentMatch[1]} />;
        }
        if (nameMatch && contentMatch) {
          return <meta key={`meta-${i}`} name={nameMatch[1]} content={contentMatch[1]} />;
        }
        return null;
      })}

      {/* Inject external scripts */}
      {externalScripts.map((script, i) => {
        const srcMatch = script.match(/src=["']([^"']*)["']/);
        const asyncMatch = script.includes('async');
        const deferMatch = script.includes('defer');
        const dataTokenMatch = script.match(/data-token=["']([^"']*)["']/);
        
        if (srcMatch) {
          return (
            <script 
              key={`script-${i}`} 
              src={srcMatch[1]} 
              async={asyncMatch} 
              defer={deferMatch}
              data-token={dataTokenMatch ? dataTokenMatch[1] : undefined}
            />
          );
        }
        return null;
      })}
    </Helmet>
  );
}
